import React, { useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useStorage } from 'hooks/storage'
import { clamp } from 'utilities/math'
import { areBasicFiltersApplied, areAdvancedFiltersApplied } from 'pages/documents/components/documents-filter'
import {
    Wrap, Header,
    GridColumnHeaderBulk, GridColumnHeaderName, GridColumnHeader, GridColumnHeaderSortable, GridColumnActionsHeader,
    SkeletonListRow, SkeletonCell, LoadingContainer, TutorialWrapper, Tutorial, Empty
} from './s'
import Checkbox from 'components/form/input/checkbox'
import Caption from 'components/typography/caption'
import Document from './document'
import Loader from 'components/loader'
import Tooltip from 'components/tooltip'
import { Ghost } from 'components/button'
import { getActionsArray } from 'widgets/context-menu'
import { SkeletonCategory, SkeletonStrings, SkeletonAvatars } from 'components/skeleton'
import ColumnConfigurator from 'components/column-configurator'

const salt = 'documents:list:configurator'

const Documents = ({
    actions = [],
    header = false, tutorial = true, empty = true,
    className
}) => {
    const { formatMessage } = useIntl()

    const {
        dokuments,
        selectable = false,
        selection,
        selectAllDocuments,
        deselectAllDocuments,
        filter,
        intersecter,
        loading,
        sorting = {},
        toggleSorting,
        paging = {},
        fetch,
        fetching,
        autoFetch,
        hasFetched,
        flash,
        clearFlash
    } = useDocuments()

    const documentsColumns = [
        {
            id: 'uploadedAt',
            title: formatMessage({
                id: 'documents_smart_heading_created',
                defaultMessage: 'Created'
            }),
            sortable: true
        },
        {
            id: 'access',
            title: formatMessage({
                id: 'documents_heading_access',
                defaultMessage: 'Access'
            })
        },
        {
            id: 'category',
            title: formatMessage({
                id: 'noun_category',
                defaultMessage: 'Category'
            })
        }
    ]

    const defaultColumnsOrder = ['uploadedAt', 'access']

    const [columnsOrder, setColumnsOrder] = useStorage({
        key: `${salt}:${header ? 'configurable' : 'default'}`,
        defaultValue: defaultColumnsOrder,
        type: 'session'
    })

    const [skeletonLength, setSkeletonLength] = useState(5)

    const hasActions = !!getActionsArray(actions).length

    const basicFiltersApplied = areBasicFiltersApplied(filter)
    const advancedFiltersApplied = areAdvancedFiltersApplied(filter)
    const filtersApplied = basicFiltersApplied || advancedFiltersApplied
    const selected = !!selection.length
    const indeterminate = !!selection.length && !!dokuments.length && selection.length !== dokuments.length

    useEffect(() => {
        if(hasFetched) {
            setSkeletonLength(clamp(dokuments.length, 1, 10))
        }
    }, [hasFetched, dokuments?.length])

    useEffect(() => {
        if(selectable) {
            const onSelectStart = event => {
                const parent = event?.target?.closest?.('.bulk-document')
                const checkbox = parent?.querySelector?.('input[type="checkbox"]')

                if(!!checkbox) {
                    event.preventDefault()
                }
            }

            document.addEventListener('selectstart', onSelectStart)
            return () => document.removeEventListener('selectstart', onSelectStart)
        }
    }, [dokuments, selection, selectable])

    return (
        <>
            <Wrap {...(className ? { className } : null)}>
                {(header || !!fetching) && (
                    <Header>
                        {(selectable && !!dokuments.length) && (
                            <Tooltip
                                content={formatMessage({
                                    id: (selected || indeterminate) ?
                                        'action_deselect_all'
                                        : 'action_select_all',
                                    defaultMessage: (selected || indeterminate) ?
                                        'Deselect all'
                                        : 'Select all'
                                })}
                                offset={[0, -12]}
                                placement="top-start"
                                trigger="mouseenter">
                                <GridColumnHeaderBulk {...(selected ? { className: 'selected' } : null)}>
                                    <Checkbox
                                        name="selected"
                                        id="documents:bulk:selected:master"
                                        checked={selected}
                                        indeterminate={indeterminate}
                                        onChange={() => {
                                            if(selected || indeterminate) {
                                                deselectAllDocuments()
                                            } else {
                                                selectAllDocuments()
                                            }
                                        }} />
                                </GridColumnHeaderBulk>
                            </Tooltip>
                        )}
                        <GridColumnHeaderName
                            field="name"
                            sort={sorting}
                            toggle={toggleSorting}
                            text={formatMessage({
                                id: 'noun_name',
                                defaultMessage: 'Name'
                            })} />
                        {columnsOrder.map((value, index) => {
                            const {
                                id,
                                title,
                                sortable = false
                            } = documentsColumns.find(({ id }) => id === value)

                            const ColumnHeader = sortable ?
                                GridColumnHeaderSortable :
                                GridColumnHeader

                            const columnHeaderProps = sortable ? {
                                text: title,
                                field: id,
                                sort: sorting,
                                toggle: toggleSorting
                            } : {
                                children: title
                            }

                            return (
                                <ColumnHeader
                                    {...columnHeaderProps}
                                    $cell={`editable${index + 1}`}
                                    key={id} />
                            )
                        })}
                        <GridColumnActionsHeader>
                            <ColumnConfigurator
                                columns={documentsColumns}
                                defaultColumnsOrder={defaultColumnsOrder}
                                columnsOrder={columnsOrder}
                                configureColumns={order => setColumnsOrder([...order])}
                                salt={salt} />
                        </GridColumnActionsHeader>
                    </Header>
                )}
                {(!!fetching && !hasFetched) && [...Array(skeletonLength).keys()].map(index => (
                    <SkeletonListRow
                        columns={3}
                        hasActions={!!hasActions}
                        key={`list:documents:skeleton:${index}`}>
                        <SkeletonCell $cell="name">
                            <SkeletonCategory size={24} showSecondLine={index % 2 === 0} />
                        </SkeletonCell>
                        <SkeletonCell $cell="editable1">
                            <SkeletonStrings size={20} count={2} />
                        </SkeletonCell>
                        <SkeletonCell $cell="editable2">
                            {index % 3 === 0 && (
                                <SkeletonAvatars size={32} />
                            )}
                            {index % 3 !== 0 && (
                                <SkeletonStrings size={20} />
                            )}
                        </SkeletonCell>
                    </SkeletonListRow>
                ))}
                {(!!tutorial && !dokuments.length && hasFetched && !filtersApplied) && (
                    <TutorialWrapper>
                        <Tutorial which="documents" />
                    </TutorialWrapper>
                )}
                {(!!empty && !dokuments.length && hasFetched && !!filtersApplied) && (
                    <Empty which="documents">
                        <Caption className="compact">
                            {basicFiltersApplied && (
                                <FormattedMessage
                                    id="documents_empty_search_query"
                                    defaultMessage={`Sorry, no documents with the title "{query}" were found.`}
                                    values={{ query: <strong>{filter.query}</strong> }} />
                            )}
                            {(advancedFiltersApplied && !basicFiltersApplied) && (
                                <FormattedMessage
                                    id="documents_empty_filter_message"
                                    defaultMessage={`🤷 Sorry, no documents found.`} />
                            )}
                        </Caption>
                    </Empty>
                )}
                {dokuments.map((dokument, index) => {
                    const last = index + 1 === dokuments.length
                    const includedColumns = columnsOrder.map(value => {
                        return { ...documentsColumns.find(({ id }) => id === value) }
                    })

                    return (
                        <Document
                            dokument={dokument}
                            actions={actions}
                            columns={includedColumns}
                            {...(last ? { ref: intersecter } : null)}
                            flash={flash?.current === dokument ? clearFlash : null}
                            key={`document:${dokument.id}`} />
                    )
                })}
            </Wrap>
            {!!paging.hasNextPage && (
                <LoadingContainer>
                    {(!loading && !autoFetch) && (
                        <Ghost className="constructive" onClick={fetch}>
                            <FormattedMessage
                                id="action_load_more"
                                defaultMessage="Load more…"
                            />
                        </Ghost>
                    )}
                    {!!loading && <Loader />}
                </LoadingContainer>
            )}
        </>
    )
}

export default Documents